import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { createB2BCoupon, getProducts, getCategories, getWarehouses } from '../config/api';
import useIsMobile from '../hooks/useIsMobile';

const CreateB2BCoupon = () => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [loading, setLoading] = useState(false);
  const [productOptions, setProductOptions] = useState([]);
  const [categoryOptions, setCategoryOptions] = useState([]);
  const [warehouseOptions, setWarehouseOptions] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(true);
  const [loadingCategories, setLoadingCategories] = useState(true);
  const [loadingWarehouses, setLoadingWarehouses] = useState(true);
  const [filteredProductOptions, setFilteredProductOptions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);
  const [couponData, setCouponData] = useState({
    code: '',
    type: 'FIXED',
    value: '',
    min_cart_value: '',
    max_discount: '',
    applicable_product_ids: [],
    applicable_category_ids: [],
    applicable_warehouse_ids: [],
    applicable_gst_nums: [],
    gst_number: '',
    valid_for_initial: '',
    usage_limit: '',
    valid_from: '',
    valid_till: '',
    active: true,
    min_delivered_orders: '',
    max_usage_per_user: '',
    last_order_since: '',
  });

  useEffect(() => {
    loadProducts();
    loadCategories();
    loadWarehouses();
    const today = new Date();
    const nextMonth = new Date(today);
    nextMonth.setMonth(today.getMonth() + 1);
    setCouponData(prev => ({
      ...prev,
      valid_from: today.toISOString().split('T')[0],
      valid_till: nextMonth.toISOString().split('T')[0]
    }));
  }, []);

  useEffect(() => {
    setFilteredProductOptions(productOptions);
  }, [productOptions]);

  const loadProducts = async () => {
    try {
      const productListRes = await getProducts();
      const allItems = productListRes.data || [];
      const prodOptions = allItems.map(item => ({
        value: item.id,
        label: `${item.skuCode} - ${item.name}`,
        product: item,
      }));
      setProductOptions(prodOptions);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadCategories = async () => {
    try {
      const categoryListRes = await getCategories();
      const options = (categoryListRes.data || []).map(category => ({
        value: category.id,
        label: category.name,
        category,
      }));
      setCategoryOptions(options);
    } catch (error) {
      console.error('Error loading categories:', error);
    } finally {
      setLoadingCategories(false);
    }
  };

  const loadWarehouses = async () => {
    try {
      const response = await getWarehouses();
      if (response && response.data) {
        const options = response.data.map(warehouse => ({
          value: warehouse.id,
          label: warehouse.name,
        }));
        setWarehouseOptions(options);
      }
    } catch (error) {
      console.error('Error loading warehouses:', error);
    } finally {
      setLoadingWarehouses(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCouponData({
      ...couponData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleProductSelection = (selectedOptions) => {
    const productIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedProducts(selectedOptions || []);
    setCouponData({ ...couponData, applicable_product_ids: productIds });
  };

  const handleCategorySelection = (selectedOptions) => {
    const categoryIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedCategories(selectedOptions || []);
    setCouponData({ ...couponData, applicable_category_ids: categoryIds });

    if (categoryIds.length > 0) {
      const filtered = productOptions.filter(option => {
        const product = option.product;
        return categoryIds.includes(product.categoryId);
      });
      setFilteredProductOptions(filtered);
      const validProducts = selectedProducts.filter(product => {
        const prod = product.product;
        return categoryIds.includes(prod.categoryId);
      });
      setSelectedProducts(validProducts);
      setCouponData(prev => ({
        ...prev,
        applicable_product_ids: validProducts.map(p => p.value),
      }));
    } else {
      setFilteredProductOptions(productOptions);
    }
  };

  const handleWarehouseSelection = (selectedOptions) => {
    const warehouseIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedWarehouses(selectedOptions || []);
    setCouponData({ ...couponData, applicable_warehouse_ids: warehouseIds });
  };

  const validateGSTNumber = (gstNumber) => {
    if (!gstNumber) return true;
    const gstRegex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
    return gstRegex.test(gstNumber);
  };

  const handleGstNumberChange = (value) => {
    const upperValue = value.toUpperCase();
    setCouponData({
      ...couponData,
      gst_number: upperValue,
      applicable_gst_nums: upperValue.trim() ? [upperValue] : [],
    });
  };

  const generateCouponCode = () => {
    const prefix = 'B2B';
    const randomString = Math.random().toString(36).substring(2, 8).toUpperCase();
    setCouponData({ ...couponData, code: `${prefix}${randomString}` });
  };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      minHeight: '42px',
      border: state.isFocused ? '2px solid #0d6efd' : '1px solid #ced4da',
      borderRadius: '6px',
      boxShadow: state.isFocused ? '0 0 0 2px rgba(13, 110, 253, 0.25)' : 'none',
      '&:hover': { borderColor: state.isFocused ? '#0d6efd' : '#ced4da' },
    }),
    placeholder: (provided) => ({ ...provided, color: '#6c757d' }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#0d6efd' : state.isFocused ? '#f8f9fa' : 'white',
      color: state.isSelected ? 'white' : '#212529',
      '&:hover': { backgroundColor: state.isSelected ? '#0d6efd' : '#f8f9fa' },
    }),
    menu: (provided) => ({ ...provided, zIndex: '9999 !important' }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
  };

  const validateForm = () => {
    if (!couponData.code.trim()) { alert('Coupon code is required'); return false; }
    if (!couponData.value || parseFloat(couponData.value) <= 0) { alert('Valid coupon value is required'); return false; }
    if (couponData.type === 'PERCENT' && parseFloat(couponData.value) > 100) { alert('Percentage discount cannot exceed 100%'); return false; }
    if (!couponData.valid_from || !couponData.valid_till) { alert('Valid from and valid till dates are required'); return false; }
    if (new Date(couponData.valid_from) >= new Date(couponData.valid_till)) { alert('Valid till date must be after valid from date'); return false; }
    if (couponData.min_cart_value && parseFloat(couponData.min_cart_value) < 0) { alert('Minimum cart value cannot be negative'); return false; }
    if (couponData.max_discount && parseFloat(couponData.max_discount) < 0) { alert('Maximum discount cannot be negative'); return false; }
    if (couponData.usage_limit && parseInt(couponData.usage_limit) < 1) { alert('Usage limit must be at least 1'); return false; }
    if (couponData.valid_for_initial && parseInt(couponData.valid_for_initial) < 1) { alert('Valid for initial orders must be at least 1'); return false; }
    if (couponData.gst_number && !validateGSTNumber(couponData.gst_number)) { alert('Invalid GST number format. Format: 22AAAAA0000A1Z5'); return false; }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setLoading(true);

    try {
      const newCoupon = {
        ...couponData,
        code: couponData.code.toUpperCase(),
        value: parseFloat(couponData.value),
        min_cart_value: couponData.min_cart_value ? parseFloat(couponData.min_cart_value) : null,
        max_discount: couponData.max_discount ? parseFloat(couponData.max_discount) : null,
        usage_limit: couponData.usage_limit ? parseInt(couponData.usage_limit) : null,
        valid_for_initial: couponData.valid_for_initial ? parseInt(couponData.valid_for_initial) : null,
        min_delivered_orders: couponData.min_delivered_orders ? parseInt(couponData.min_delivered_orders) : null,
        max_usage_per_user: couponData.max_usage_per_user ? parseInt(couponData.max_usage_per_user) : null,
        last_order_since: couponData.last_order_since ? parseInt(couponData.last_order_since) : null,
        applicable_product_ids: couponData.applicable_product_ids.length > 0 ? couponData.applicable_product_ids : [],
        applicable_category_ids: couponData.applicable_category_ids.length > 0 ? couponData.applicable_category_ids : [],
        applicable_warehouse_ids: couponData.applicable_warehouse_ids.length > 0 ? couponData.applicable_warehouse_ids : [],
        applicable_gst_nums: couponData.applicable_gst_nums.length > 0 ? couponData.applicable_gst_nums : [],
      };

      // Remove UI-only fields
      delete newCoupon.gst_number;

      const response = await createB2BCoupon(newCoupon) || {};
      if (response.error) {
        alert(response.error || 'Some error occurred');
        return;
      }
      alert(`B2B Coupon ${newCoupon.code} created successfully!`);
      navigate('/b2b-coupons');
    } catch (error) {
      console.error('Error creating B2B coupon:', error);
      alert(error.message || 'Failed to create B2B coupon. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="card">
        <div className="order-detail-header">
          <div className="order-detail-title">
            <div className="breadcrumb">
              <Link to="/b2b-coupons">B2B Coupons</Link> / Create New B2B Coupon
            </div>
            <h1>Create New B2B Coupon</h1>
          </div>
          <div className="order-actions">
            <Link to="/b2b-coupons" className="btn btn-secondary" style={{ textDecoration: 'none' }}>
              Cancel
            </Link>
            <button type="submit" form="create-b2b-coupon-form" disabled={loading} className="btn btn-success">
              {loading ? 'Creating...' : 'Create B2B Coupon'}
            </button>
          </div>
        </div>

        <form id="create-b2b-coupon-form" onSubmit={handleSubmit}>
          {/* Basic Information */}
          <div className="card">
            <h3 className="section-title">Basic Information</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Coupon Code *</label>
                <div style={{ display: 'flex', gap: '10px' }}>
                  <input
                    type="text" name="code" className="form-control"
                    value={couponData.code} onChange={handleInputChange}
                    required placeholder="Enter coupon code"
                    autoCapitalize="characters" autoCorrect="off" spellCheck={false}
                    style={{ textTransform: 'uppercase', flex: 1 }}
                  />
                  <button type="button" onClick={generateCouponCode} className="btn btn-primary"
                    style={{ fontSize: '12px', padding: '8px 16px' }}>
                    Generate
                  </button>
                </div>
              </div>

              <div className="info-item">
                <label className="info-label">Discount Type *</label>
                <select name="type" className="form-control" value={couponData.type} onChange={handleInputChange} required>
                  <option value="FIXED">Fixed</option>
                  <option value="PERCENT">Percentage</option>
                </select>
              </div>

              <div className="info-item">
                <label className="info-label">
                  Discount Value * {couponData.type === 'FIXED' ? '(₹)' : '(%)'}
                </label>
                <input
                  type="number" name="value" className="form-control"
                  value={couponData.value} onChange={handleInputChange}
                  required min="0"
                  step={couponData.type === 'FIXED' ? '0.01' : '1'}
                  max={couponData.type === 'PERCENT' ? '100' : undefined}
                  placeholder={couponData.type === 'FIXED' ? 'Enter amount' : 'Enter percentage'}
                />
              </div>

              <div className="info-item">
                <label className="info-label">Status</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <input type="checkbox" name="active" checked={couponData.active} onChange={handleInputChange} style={{ width: 'auto' }} />
                  <span>{couponData.active ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Conditions */}
          <div className="card">
            <h3 className="section-title">Conditions & Limits</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Minimum Cart Value (₹)</label>
                <input type="number" name="min_cart_value" className="form-control"
                  value={couponData.min_cart_value} onChange={handleInputChange}
                  min="0" step="0.01" placeholder="No minimum (optional)" />
              </div>

              <div className="info-item">
                <label className="info-label">Maximum Discount (₹)</label>
                <input type="number" name="max_discount" className="form-control"
                  value={couponData.max_discount} onChange={handleInputChange}
                  min="0" step="0.01" placeholder="No limit (optional)" />
              </div>

              <div className="info-item">
                <label className="info-label">Usage Limit</label>
                <input type="number" name="usage_limit" className="form-control"
                  value={couponData.usage_limit} onChange={handleInputChange}
                  min="1" placeholder="Unlimited (optional)" />
              </div>

              <div className="info-item">
                <label className="info-label">Valid for Initial Orders</label>
                <input type="number" name="valid_for_initial" className="form-control"
                  value={couponData.valid_for_initial} onChange={handleInputChange}
                  min="1" placeholder="All orders (optional)" />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Limit coupon to first N B2B orders of a customer
                </small>
              </div>

              <div className="info-item">
                <label className="info-label">Min Delivered Orders</label>
                <input type="number" name="min_delivered_orders" className="form-control"
                  value={couponData.min_delivered_orders} onChange={handleInputChange}
                  min="1" placeholder="No minimum (optional)" />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Requires minimum delivered B2B orders to use this coupon
                </small>
              </div>

              <div className="info-item">
                <label className="info-label">Max Usage Per User</label>
                <input type="number" name="max_usage_per_user" className="form-control"
                  value={couponData.max_usage_per_user} onChange={handleInputChange}
                  min="1" placeholder="Unlimited (optional)" />
              </div>

              <div className="info-item">
                <label className="info-label">Last Order Since (days)</label>
                <input type="number" name="last_order_since" className="form-control"
                  value={couponData.last_order_since} onChange={handleInputChange}
                  min="1" placeholder="No restriction (optional)" />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Only valid if user has not placed a B2B order in last X days
                </small>
              </div>
            </div>
          </div>

          {/* Validity Period */}
          <div className="card">
            <h3 className="section-title">Validity Period</h3>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Valid From *</label>
                <input type="date" name="valid_from" className="form-control"
                  value={couponData.valid_from} onChange={handleInputChange} required />
              </div>
              <div className="info-item">
                <label className="info-label">Valid Till *</label>
                <input type="date" name="valid_till" className="form-control"
                  value={couponData.valid_till} onChange={handleInputChange} required />
              </div>
            </div>
          </div>

          {/* Applicable Products & Categories */}
          <div className="card">
            <h3 className="section-title">Applicable Products & Categories</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Select categories first to filter products, or leave empty to apply to all:
            </p>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Select Categories</label>
                <Select
                  value={selectedCategories} onChange={handleCategorySelection}
                  options={categoryOptions} styles={selectStyles}
                  placeholder={loadingCategories ? 'Loading categories...' : 'Select categories...'}
                  isSearchable isClearable isMulti
                  isLoading={loadingCategories} isDisabled={loadingCategories}
                  noOptionsMessage={() => 'No categories found'}
                  menuPortalTarget={document.body}
                />
              </div>
              <div className="info-item">
                <label className="info-label">Select Products</label>
                <Select
                  value={selectedProducts} onChange={handleProductSelection}
                  options={filteredProductOptions} styles={selectStyles}
                  placeholder={loadingProducts ? 'Loading products...' : 'Select products...'}
                  isSearchable isClearable isMulti
                  isLoading={loadingProducts} isDisabled={loadingProducts}
                  noOptionsMessage={() => selectedCategories.length > 0 ? 'No products in selected categories' : 'No products found'}
                  menuPortalTarget={document.body}
                />
              </div>
            </div>
          </div>

          {/* Applicable Warehouses */}
          <div className="card">
            <h3 className="section-title">Applicable Warehouses</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Select warehouses where this coupon is valid, or leave empty to apply to all:
            </p>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">Select Warehouses</label>
                <Select
                  value={selectedWarehouses} onChange={handleWarehouseSelection}
                  options={warehouseOptions} styles={selectStyles}
                  placeholder={loadingWarehouses ? 'Loading warehouses...' : 'Select warehouses...'}
                  isSearchable isClearable isMulti
                  isLoading={loadingWarehouses} isDisabled={loadingWarehouses}
                  noOptionsMessage={() => 'No warehouses found'}
                  menuPortalTarget={document.body}
                />
                <small style={{ color: '#6c757d', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                  Empty selection means coupon is valid for all warehouses
                </small>
              </div>
            </div>
          </div>

          {/* GST Targeting */}
          <div className="card">
            <h3 className="section-title">B2B Customer Targeting</h3>
            <p style={{ color: '#6c757d', marginBottom: '20px' }}>
              Leave empty to apply to all B2B customers, or specify a GST number for targeting:
            </p>
            <div className="order-info">
              <div className="info-item">
                <label className="info-label">GST Number</label>
                <input
                  type="text" name="gst_number" className="form-control"
                  value={couponData.gst_number || ''}
                  onChange={(e) => handleGstNumberChange(e.target.value)}
                  placeholder="22AAAAA0000A1Z5 (optional)" maxLength="15"
                  autoCapitalize="characters" autoCorrect="off"
                  style={{
                    textTransform: 'uppercase',
                    borderColor: couponData.gst_number && !validateGSTNumber(couponData.gst_number) ? '#dc3545' : '#ced4da',
                  }}
                />
                {couponData.gst_number && !validateGSTNumber(couponData.gst_number) && (
                  <small style={{ color: '#dc3545', fontSize: '12px', marginTop: '4px', display: 'block' }}>
                    Invalid GST format. Format: 22AAAAA0000A1Z5
                  </small>
                )}
              </div>
            </div>
          </div>
          {isMobile && (
            <div className="m-sticky-actions">
              <Link to="/b2b-coupons" className="btn btn-secondary" style={{ textDecoration: 'none' }}>
                Cancel
              </Link>
              <button type="submit" disabled={loading} className="btn btn-success">
                {loading ? 'Creating...' : 'Create B2B Coupon'}
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default CreateB2BCoupon;
